const toNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundHours = (value: number): number => Number(value.toFixed(2));

export interface InstallationLabourSplit {
  skilledHours: number;
  semiSkilledHours: number;
  nonWorkingHours: number;
}

const INSTALLATION_LABOUR_ROW_HOURS: Record<string, keyof InstallationLabourSplit> = {
  'Direct Manhours Skilled': 'skilledHours',
  'Direct Manhours Semi/Unskilled (Working Days Only)': 'semiSkilledHours',
  'Non Working Days': 'nonWorkingHours',
};

export const isInstallationLabourAutoRow = (subCategory: unknown): boolean => {
  return typeof subCategory === 'string' && subCategory in INSTALLATION_LABOUR_ROW_HOURS;
};

export const sumInstallationPartManhours = (
  parts: Array<{ man_hours_required?: unknown }> | null | undefined,
): number => {
  return (parts || []).reduce((sum, part) => sum + toNumber(part?.man_hours_required), 0);
};

export const calculateInstallationLineCost = (
  manHours: unknown,
  quantity: unknown,
  unitRate: unknown,
): number => {
  const mh = toNumber(manHours);
  const qty = toNumber(quantity);
  const rate = toNumber(unitRate);

  if (mh > 0 && qty > 0) return mh * qty * rate;
  if (mh > 0) return mh * rate;
  return qty * rate;
};

export const computeInstallationLabourSplit = (
  totalManhours: unknown,
  skilledPercent: unknown,
  semiSkilledPercent: unknown,
  nonWorkingHoursPerWeek: unknown,
): InstallationLabourSplit => {
  const total = toNumber(totalManhours);
  if (total <= 0) {
    return {
      skilledHours: 0,
      semiSkilledHours: 0,
      nonWorkingHours: 0,
    };
  }

  const skilled = total * (toNumber(skilledPercent) / 100);
  const semiSkilled = total * (toNumber(semiSkilledPercent) / 100);
  const nonWorkingHours = total * (toNumber(nonWorkingHoursPerWeek) / 48);

  return {
    skilledHours: roundHours(skilled),
    semiSkilledHours: roundHours(semiSkilled),
    nonWorkingHours: roundHours(nonWorkingHours),
  };
};

export const applyInstallationLabourSplitToItems = <T extends { sub_category?: unknown; man_hours?: unknown; quantity?: unknown; unit_rate?: unknown; cost?: unknown }>(
  items: T[] | null | undefined,
  split: InstallationLabourSplit,
): T[] => {
  return (items || []).map((item) => {
    const splitKey =
      typeof item.sub_category === 'string'
        ? INSTALLATION_LABOUR_ROW_HOURS[item.sub_category]
        : undefined;

    if (!splitKey) return item;

    const manHours = toNumber(split[splitKey]);
    return {
      ...item,
      man_hours: manHours,
      cost: calculateInstallationLineCost(manHours, item.quantity, item.unit_rate),
    };
  });
};
